import { useEffect, useRef, useState } from 'react'

/**
 * Re-reads something on a beat, while there is a reason to.
 *
 * The polling lived spread across the queue's body -- a counter, a ref, an
 * interval and a visibility listener -- which made "when does this refresh"
 * a question you answered by reading the whole ingredient. It is one behaviour,
 * so it is one thing here, and any other screen that needs it says so in a
 * line.
 *
 * **`takeBackground` is the point.** A refresh nobody asked for must not
 * announce itself: the queue used to blank its table to "carregando" every
 * three seconds for news that usually amounted to nothing. The caller asks,
 * once per read, whether this read came from the beat or from a person, and
 * shows a wait only for the second kind.
 */
export interface AutoRefresh {
  /** Changes when it is time to read again. Put it in the reading effect's deps. */
  token: number
  /**
   * Whether the read now beginning came from the beat, clearing the flag as it
   * answers -- so the next read is a person's until something says otherwise.
   */
  takeBackground(): boolean
  /** Marks the next read as background: for a write that reloads quietly. */
  markBackground(): void
  /** Read again now, as though a person asked. */
  refresh(): void
}

export function useAutoRefresh(active: boolean, everyMs = 5000): AutoRefresh {
  const [token, setToken] = useState(0)
  const background = useRef(false)
  const control = useRef<AutoRefresh>({
    token,
    takeBackground() {
      const was = background.current
      background.current = false
      return was
    },
    markBackground() {
      background.current = true
    },
    /*
     * Asks for a read now. Does not decide how loud it is.
     *
     * This used to clear the background flag first, which undid the
     * `markBackground()` immediately before it -- every "reload quietly" pair
     * in the app was a loud reload, blanking the table under whoever had just
     * pressed something. "Atualizar na coluna status está dando refresh na
     * tela" was this line.
     *
     * The flag is cleared by `takeBackground()` when the read consumes it, so
     * it is still true exactly once per mark: a later automatic tick is loud
     * again, and nothing has to remember to reset it.
     */
    refresh() {
      setToken((n) => n + 1)
    },
  })

  // The object identity is stable so callers can hold it without re-running
  // effects; only the token inside it moves.
  control.current.token = token

  useEffect(() => {
    if (!active) return undefined

    const beat = () => {
      // Paused while the tab is elsewhere. A screen left open overnight would
      // otherwise spend the night asking.
      if (document.visibilityState !== 'visible') return
      background.current = true
      setToken((n) => n + 1)
    }

    const timer = setInterval(beat, everyMs)
    // And once on coming back, so returning to the tab shows the current status
    // rather than whatever it held when you left.
    document.addEventListener('visibilitychange', beat)
    return () => {
      clearInterval(timer)
      document.removeEventListener('visibilitychange', beat)
    }
  }, [active, everyMs])

  return control.current
}
